'use client';

import { TBiometricConfidenceLevel } from '@verifime/components';
import { useCustomerConfidenceContext } from './CustomerConfidenceContext';
import { TRequestState } from './requestState';
import { useFaceCapture } from './useFaceCapture';
import { useFaceCheckTasks } from './useFaceCheckTasks';
import { useIdPortraitEvidence } from './useIdPortraitEvidence';
import { useRequestPermission } from './useRequestPermission';

/**
 * The biometric levels worth fetching a real capture for.
 *
 * Every level's card carries the thumbnail slot (`EvidenceThumbnail` shows its own "No capture yet"
 * placeholder when there is nothing to display), so this does not decide whether a thumbnail appears
 * at all, only whether it is worth the call.
 *
 * `NotCompleted` has no capture at all. `InProgress` does have one, but is excluded deliberately:
 * that level means the check completed while an input it needs is still missing, so the card is
 * reporting an unfinished picture and a face on it would read as a settled result. Both levels fall
 * through to the placeholder rather than a fetched image.
 */
const LEVELS_WITH_CAPTURE_TO_FETCH: readonly TBiometricConfidenceLevel[] = ['High', 'Low'];

export type TUseConfidenceCardsParams = {
  customerId?: string;
  organisationId?: string;
  onError?: (error: unknown) => void;
};

/**
 * Everything the customer's two confidence cards need to know, gathered in one place.
 *
 * **Extracted from `CustomerConfidenceCards` on Charles's review of PR #2085**: "consider extract
 * the logic to a hook to keep the component simple". What moved is exactly the derivations — the
 * context read, the four data hooks, and the five values computed from them. What did not move is
 * anything that builds an element: a hook returning a `ReactElement` would read as a mistake at its
 * type signature and could not be exercised outside React, so the dialogs' shared request button
 * stays in the view that renders it.
 *
 * **The two dialog open/close flags also stayed.** They are state about dialogs this component
 * renders and nothing else consults, so moving them would only add two setters to this hook's
 * surface for no reader's benefit.
 *
 * **Every hook below is called unconditionally, in a fixed order.** The two that are conditional in
 * effect — the capture and the identity portrait — take that condition as an `enabled` argument, the
 * convention already used by `AddressAutocompleteView` and by these hooks' own signatures, rather
 * than being wrapped in an `if`. None of them moved inside a branch.
 *
 * **The order is not identical to the component's before this extraction, and the difference is
 * worth naming rather than glossing.** The two dialog `useState`s used to sit between
 * `useRequestPermission` and the two fetches; they now come after all four, because they stayed in
 * the component while the four moved here. React requires the order to be the same on every render
 * of the same code, not the same as some earlier version of it, so this is not a rules-of-hooks
 * hazard — but "unchanged order" would have been the wrong words for it, and a comment that is
 * confidently wrong is worse than one that is vague.
 */
export function useConfidenceCards({
  customerId,
  organisationId,
  onError,
}: TUseConfidenceCardsParams) {
  // From the provider, not from a call of its own. The document level also appears on the KYC/KYB
  // section further down the page, and these two are the summary and the detail of one fact: with a
  // call each they would light up at different moments and read as two statements about one check.
  // The confidence levels stay the provider's to hold; the organisation id comes in as a parameter
  // because the permission lookup below needs it and the provider does not publish it.
  const { document: documentAxis, biometric: biometricAxis } = useCustomerConfidenceContext();
  const { requestState, requestFaceCheck } = useFaceCheckTasks({ customerId, onError });
  const { isResolving, requestPermission } = useRequestPermission({ customerId, organisationId });

  // Only a confirmed live permission offers the control — or an admin, whom person-api authorises by
  // role rather than by any organisation's permission. Every other outcome withholds it: a
  // permission that is not `Active`, no permission at all, a lookup that failed for any reason, or
  // one that has not landed yet. Settled by the owner on 2026-08-26, whose reasoning was that a
  // control which cannot work is not worth offering. The withheld cases are alike in what the card
  // offers and unalike in what it says.
  const mayRequest = requestPermission === 'permitted';

  // Fetched only for the levels worth it, so a customer with no completed face check costs no call.
  // This is one endpoint, not the dialog's four: see `useFaceCapture` for why the card must not
  // reuse the evidence gather.
  const hasCaptureToFetch =
    biometricAxis.status === 'ready' &&
    LEVELS_WITH_CAPTURE_TO_FETCH.includes(biometricAxis.level as TBiometricConfidenceLevel);
  const faceCapture = useFaceCapture({ customerId, enabled: hasCaptureToFetch, onError });

  // Fetched only at `NotCompleted`, the one level with no face-check capture and therefore the only
  // one `VIEW ID PORTRAIT` can ever appear for. Called here rather than inside `IdPortraitEvidence`
  // so the same fetch both gates the button and, once fetched, supplies the dialog's content: see
  // `useIdPortraitEvidence`'s docblock.
  const isNotCompleted = biometricAxis.status === 'ready' && biometricAxis.level === 'NotCompleted';
  const idPortraitEvidence = useIdPortraitEvidence({
    customerId,
    enabled: isNotCompleted,
    onError,
  });
  const hasIdPortrait = Boolean(idPortraitEvidence.portraitUrl);

  // Truthy only once a real capture is behind the thumbnail, not merely once fetching started
  // (`hasCaptureToFetch` alone is also true for the still-loading window). Drives both the
  // thumbnail's `src` and the card's flex weight, so the two cannot drift apart: a wider column and
  // no image, or an image in a column sized for the placeholder, are each their own small lie about
  // what the card is showing.
  const mediaSrc = hasCaptureToFetch ? faceCapture.src : undefined;

  return {
    documentAxis,
    biometricAxis,
    // Handed back alongside `mayRequest` rather than folded into it. The card needs both: one
    // decides whether the control renders, the other decides which of two sentences the notice
    // reads, and `unknown` and `notPermitted` are the same answer to the first question and
    // different answers to the second.
    requestPermission,
    requestState: requestState as TRequestState,
    requestFaceCheck,
    isResolving,
    mayRequest,
    isNotCompleted,
    hasIdPortrait,
    portraitUrl: idPortraitEvidence.portraitUrl,
    mediaSrc,
  };
}
